import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../../../nurse-dashboard/theme";
import { usePermissions } from "../../../login/PermissionContext";
import { getSectionConfiguration, saveSectionConfiguration } from "../../api/sectionConfigurationApi";

const CONFIGURATION_TYPE = "auto_housekeeping";

// Admin toggle for the auto-housekeeping setting (ADR 0002) — whether a bed
// goes straight to "cleaning" instead of "available" when freed on
// discharge or transfer.
export default function HousekeepingSettingsAdmin() {
  const { userInfo } = usePermissions();
  const facilityId = userInfo?.facility_id;

  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await getSectionConfiguration(facilityId, CONFIGURATION_TYPE);
      setEnabled(Boolean(res?.data?.configuration?.enabled));
    } catch (err) {
      console.error("Failed to load housekeeping settings:", err);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (e) => {
    const next = e.target.checked;
    setEnabled(next);
    setSaving(true);
    try {
      const res = await saveSectionConfiguration(facilityId, CONFIGURATION_TYPE, { enabled: next });
      if (res?.status === "Success") {
        toast.success(next ? "Auto-housekeeping enabled." : "Auto-housekeeping disabled.");
      } else {
        setEnabled(!next);
        toast.error(res?.message || "Could not save the setting.");
      }
    } catch (err) {
      setEnabled(!next);
      toast.error(err?.response?.data?.message || "Could not save the setting.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Housekeeping Settings</Typography>
      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, maxWidth: 520 }}>
        <FormControlLabel
          control={
            <Switch checked={enabled} onChange={handleToggle} disabled={loading || saving || !facilityId} />
          }
          label="Auto-send freed beds to housekeeping"
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          When enabled, a bed goes straight to "Cleaning" status the instant
          it's freed on discharge or transfer, instead of "Available" —
          skipping the manual mark-for-cleaning step.
        </Typography>
        {!facilityId && !loading && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Could not resolve your facility — try refreshing the page.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
